import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { createRoadmapGroup, updateRoadmapGroup, deleteRoadmapGroup } from '../graphql/mutations';
import { listRoadmapGroups } from '../graphql/queries';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles } from '@material-ui/core/styles';
import S3Image from './S3Image';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6),
    },
  },
  textField: {
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  uploadButton: {
    margin: theme.spacing(1),
  },
  submitButton: {
    margin: theme.spacing(3, 0),
  },
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginTop: '32px',
    backgroundColor: '#f9f9f9',
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem',
  },
  actionButton: {
    color: '#01b5e2',
    borderColor: '#01b5e2',
    '&:hover': {
      backgroundColor: '#01b5e2',
      color: 'white',
    }
  },
}));

const AddRoadmapForm = () => {
  const classes = useStyles();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailKey, setThumbnailKey] = useState('');
  const [roadmapGroups, setRoadmapGroups] = useState([]);
  const [editingRoadmapGroupId, setEditingRoadmapGroupId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    fetchRoadmapGroups();
  }, []);

  const fetchRoadmapGroups = async () => {
    const response = await API.graphql(graphqlOperation(listRoadmapGroups));
    setRoadmapGroups(response.data.listRoadmapGroups.items);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let newThumbnailKey = '';

    if (thumbnail) {
      const ext = thumbnail.name.split('.').pop();
      newThumbnailKey = `thumbnails/${uuidv4()}.${ext}`;
      await Storage.put(newThumbnailKey, thumbnail);
      setThumbnailKey(newThumbnailKey);
    }

    if (editingRoadmapGroupId) {
      await API.graphql(
        graphqlOperation(updateRoadmapGroup, {
          input: {
            id: editingRoadmapGroupId,
            name,
            description,
            imageS3ObjectKey: newThumbnailKey || null,
          },
        })
      );
    } else {
      await API.graphql(
        graphqlOperation(createRoadmapGroup, {
          input: {
            name,
            description,
            imageS3ObjectKey: newThumbnailKey || null,
          },
        })
      );
    }

    setName('');
    setDescription('');
    setThumbnail(null);
    setThumbnailKey('');
    setEditingRoadmapGroupId(null);
    fetchRoadmapGroups();
  };

  const handleEdit = (roadmapGroupId) => {
    const roadmapGroup = roadmapGroups.find((group) => group.id === roadmapGroupId);
    if (roadmapGroup) {
      setName(roadmapGroup.name);
      setDescription(roadmapGroup.description);
      setThumbnailKey(roadmapGroup.imageS3ObjectKey);
      setEditingRoadmapGroupId(roadmapGroupId);
    }
  };

  const handleCancelEdit = () => {
    setName('');
    setDescription('');
    setThumbnail(null);
    setThumbnailKey('');
    setEditingRoadmapGroupId(null);
  };

  const handleDelete = async () => {
    await API.graphql(graphqlOperation(deleteRoadmapGroup, { input: { id: deleteTargetId } }));
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
    fetchRoadmapGroups();
  };

  const handleThumbnailChange = (event) => {
    setThumbnail(event.target.files[0]);
  };

  return (
    <div>
      <Box className={classes.root} component="form" onSubmit={handleSubmit}>
        <Typography variant="h4" component="h1" gutterBottom>
          {editingRoadmapGroupId ? 'Edit Roadmap Group' : 'Add Roadmap Group'}
        </Typography>
        <TextField
          className={classes.textField}
          required
          label="Group Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          className={classes.textField}
          label="Description"
          multiline
          minRows={4}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Button
          className={classes.uploadButton}
          variant="contained"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          Upload Image
          <input type="file" hidden onChange={handleThumbnailChange} />
        </Button>
        {thumbnail && <Typography>{thumbnail.name}</Typography>}
        {thumbnailKey && (
          <Box mt={2}>
            <Typography variant="h6">Current Thumbnail Preview:</Typography>
            <S3Image S3Key={thumbnailKey} S3height={200} />
          </Box>
        )}
        <Button type="submit" variant="contained" color="primary">
          {editingRoadmapGroupId ? 'Update' : 'Submit'}
        </Button>
        {editingRoadmapGroupId && (
          <Button variant="outlined" color="secondary" onClick={handleCancelEdit} style={{ marginTop: '10px' }}>
            Cancel
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableCell}>Name</TableCell>
              <TableCell className={classes.tableCell}>Description</TableCell>
              <TableCell className={classes.tableCell}>Image</TableCell>
              <TableCell className={classes.tableCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roadmapGroups.map((roadmapGroup) => (
              <TableRow key={roadmapGroup.id} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{roadmapGroup.name}</TableCell>
                <TableCell className={classes.tableCell}>{roadmapGroup.description}</TableCell>
                <TableCell className={classes.tableCell}>
                  {roadmapGroup.imageS3ObjectKey && (
                    <S3Image S3Key={roadmapGroup.imageS3ObjectKey} S3height={100} />
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Button variant="outlined" className={classes.actionButton} onClick={() => handleEdit(roadmapGroup.id)}>Edit</Button>
                  <Button variant="outlined" className={classes.actionButton} onClick={() => { setDeleteDialogOpen(true); setDeleteTargetId(roadmapGroup.id); }}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this roadmap group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddRoadmapForm;
